import { createFileRoute } from "@tanstack/react-router";
import { usePageTitle } from "@/hooks/use-page-title";
import { useGuildRoute } from "@/hooks/use-guild-route";
import { AlertTriangle } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  KBStatus,
  KBDocuments,
  KBPersonality,
  KBSettings,
  KBEvaluation,
  ForumSources,
} from "@/components/knowledge-base";
import { UpgradeBanner } from "@/components/subscription/upgrade-banner";
import {
  knowledgeBaseStatusQueryOptions,
  guildChannelsQueryOptions,
  type KnowledgeBaseStatus,
} from "@/lib/api";
import { useGuildSubscriptionFeatures } from "@/hooks/use-guild-subscription-features";
import { useGuildPermissions } from "@/hooks/use-guild-permissions";
import { useQuery } from "@tanstack/react-query";

export const Route = createFileRoute(
  "/dashboard/guilds/$applicationGuildId/knowledge-base"
)({
  component: KnowledgeBasePage,
});

function KnowledgeBasePage() {
  usePageTitle("Knowledge Base");
  const { applicationGuildId, isLocked } = useGuildRoute();

  const { data: kbStatus, isPending: kbPending } = useQuery(
    knowledgeBaseStatusQueryOptions(applicationGuildId)
  );

  const { isFreePlan, limits, usage } = useGuildSubscriptionFeatures();
  const { canEdit } = useGuildPermissions();
  const isReadOnly = isLocked || !canEdit("knowledgeBase");

  const status = kbStatus?.data;

  const docCount = usage?.documents ?? status?.documentsCount ?? 0;
  const docLimit = limits?.maxDocumentsPerGuild ?? 50;
  const usagePercent = docLimit > 0 ? (docCount / docLimit) * 100 : 0;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-balance text-3xl font-bold">Knowledge Base</h1>
        <p className="text-muted-foreground text-pretty">
          Manage your bot&apos;s documentation and AI personality
        </p>
      </div>

      {isFreePlan && usagePercent >= 80 && (
        <UpgradeBanner
          feature={`${docCount}/${docLimit} documents used`}
          description="You're approaching your document limit. Upgrade to Pro for up to 500 documents."
        />
      )}

      {!isFreePlan && usagePercent >= 80 && (
        <div className="flex items-center gap-2 rounded-lg border border-amber-500/20 bg-amber-500/5 p-3 text-sm">
          <AlertTriangle className="size-4 shrink-0 text-amber-500" />
          <span className="text-muted-foreground">
            {docCount}/{docLimit} documents used — approaching your plan limit.
          </span>
        </div>
      )}

      <Tabs defaultValue="status" className="space-y-6">
        <TabsList>
          <TabsTrigger value="status">Status</TabsTrigger>
          <TabsTrigger value="documents">Documents</TabsTrigger>
          <TabsTrigger value="sources">Sources</TabsTrigger>
          <TabsTrigger value="personality">Personality</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
          <TabsTrigger value="evaluation">Evaluation</TabsTrigger>
        </TabsList>

        <TabsContent value="status" className="space-y-6">
          {status ? (
            <StatusTab
              status={status}
              applicationGuildId={applicationGuildId}
              isLoading={kbPending}
            />
          ) : (
            <div className="space-y-6">
              {/* Stats cards skeleton */}
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {[1, 2, 3, 4].map((i) => (
                  <div
                    key={i}
                    className="bg-card space-y-3 rounded-xl border p-4"
                  >
                    <div className="flex items-center justify-between">
                      <Skeleton className="h-4 w-20" />
                      <Skeleton className="size-8 rounded-lg" />
                    </div>
                    <Skeleton className="h-7 w-12" />
                    <Skeleton className="h-3 w-24" />
                  </div>
                ))}
              </div>
              {/* Channel list skeleton */}
              <div className="bg-card space-y-4 rounded-xl border p-5">
                <div className="flex items-center gap-3">
                  <Skeleton className="size-8 rounded-lg" />
                  <Skeleton className="h-5 w-40" />
                </div>
                {[1, 2, 3].map((i) => (
                  <div key={i} className="flex items-center gap-3 py-2">
                    <Skeleton className="size-5 rounded" />
                    <Skeleton className="h-4 w-32" />
                    <div className="ml-auto">
                      <Skeleton className="h-6 w-16 rounded-full" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="documents">
          <KBDocuments
            applicationGuildId={applicationGuildId}
            isLocked={isReadOnly}
          />
        </TabsContent>

        <TabsContent value="sources">
          <ForumSources
            applicationGuildId={applicationGuildId}
            isLocked={isReadOnly}
          />
        </TabsContent>

        <TabsContent value="personality">
          <KBPersonality
            applicationGuildId={applicationGuildId}
            documentsCount={status?.documentsCount || 0}
            isLocked={isReadOnly}
          />
        </TabsContent>

        <TabsContent value="settings">
          <KBSettings
            applicationGuildId={applicationGuildId}
            settings={status?.settings || null}
            isLocked={isReadOnly}
          />
        </TabsContent>

        <TabsContent value="evaluation">
          <KBEvaluation
            applicationGuildId={applicationGuildId}
            isLocked={isReadOnly}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function StatusTab({
  status,
  applicationGuildId,
  isLoading,
}: {
  status: KnowledgeBaseStatus;
  applicationGuildId: string;
  isLoading: boolean;
}) {
  const { data: channelsData } = useQuery(
    guildChannelsQueryOptions(applicationGuildId)
  );
  return (
    <KBStatus
      status={status}
      channels={channelsData?.data}
      isLoading={isLoading}
    />
  );
}
